import { FloodIt } from '@minigame-race/contracts';
import type { MinigameProps } from '../../race/types.js';
import { color } from '../../theme.js';

/**
 * Flood-It. The board and move count are authoritative from the server; the
 * client only paints and emits colour picks. No optimistic prediction — a flood
 * is cheap to round-trip and mispredicting a whole-board repaint looks worse
 * than a ~1 RTT delay.
 */

function isView(v: unknown): v is FloodIt.FloodPrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'flood-it';
}

export function FloodItGame({ view, emit }: MinigameProps) {
  const s = isView(view) ? view : null;
  if (!s) return null;

  const currentColor = s.cells[0] ?? 0;
  const overPar = Math.max(0, s.movesUsed - s.par);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 'min(90cqmin,440px)',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(10px,2.2cqmin,18px)',
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, fontFamily: "'JetBrains Mono',monospace" }}>
        <span style={{ fontSize: 'clamp(13px,2.6cqmin,18px)', fontWeight: 700, color: overPar > 0 ? color.amberBright : color.lime }}>
          {s.movesUsed}
          <span style={{ opacity: 0.5 }}> / {s.par}</span>
        </span>
        <span style={{ fontSize: 'clamp(9px,1.7cqmin,12px)', letterSpacing: '.14em', color: 'rgba(234,255,234,.45)' }}>
          MOVES {overPar > 0 ? `(+${overPar} over)` : ''}
        </span>
      </div>

      {/* board */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${s.size}, 1fr)`,
          gap: 'clamp(1px,0.4cqmin,3px)',
          width: '100%',
          aspectRatio: '1 / 1',
          padding: 'clamp(4px,1cqmin,8px)',
          borderRadius: 12,
          background: 'rgba(255,255,255,.03)',
          border: '1px solid rgba(166,255,46,.14)',
        }}
      >
        {s.cells.map((c, i) => (
          <div
            key={i}
            style={{
              background: FloodIt.FLOOD_PALETTE[c],
              borderRadius: 'clamp(2px,0.8cqmin,5px)',
              // Captured cells get a subtle inner ring so the blob is visible.
              boxShadow: s.captured[i] ? 'inset 0 0 0 2px rgba(255,255,255,.55)' : undefined,
              transition: 'background .18s ease',
            }}
          />
        ))}
      </div>

      {/* colour palette */}
      <div style={{ display: 'flex', gap: 'clamp(6px,1.6cqmin,12px)', flexWrap: 'wrap', justifyContent: 'center' }}>
        {FloodIt.FLOOD_PALETTE.slice(0, FloodIt.FLOOD_COLORS).map((hex, ci) => {
          const isCurrent = ci === currentColor;
          return (
            <button
              key={ci}
              type="button"
              disabled={isCurrent || s.isComplete}
              onClick={() => emit({ type: 'pick', color: ci })}
              aria-label={`Flood colour ${ci + 1}`}
              style={{
                width: 'clamp(36px,9cqmin,54px)',
                height: 'clamp(36px,9cqmin,54px)',
                borderRadius: '50%',
                background: hex,
                border: isCurrent ? '3px solid #fff' : '3px solid rgba(0,0,0,.35)',
                opacity: s.isComplete ? 0.4 : isCurrent ? 0.55 : 1,
                cursor: isCurrent || s.isComplete ? 'default' : 'pointer',
                transition: 'transform .1s ease',
                touchAction: 'manipulation',
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
